import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { fileUrl, resolveUrl } from "@/lib/api";
import { UploadCloud, Loader2, CheckCircle2, Image as ImageIcon, Save, Send } from "lucide-react";
import { toast } from "sonner";

const empty = {
  title: { fr: "", en: "" },
  description: { fr: "", en: "" },
  category_id: "",
  creator_id: "",
  tags: [],
  thumbnail_url: "",
  is_sponsored: false,
  sponsor_name: "",
  is_featured: false,
  is_hero: false,
  hero_order: 0,
  featured_order: 0,
  publish_at: "",
  unpublish_at: "",
  ecosystem: { partner_id: "", academy_module_id: "", event_id: "", campaign_id: "", external_cta_url: "" },
};

const field = "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/50";
const lbl = "mb-1.5 block text-xs uppercase tracking-widest text-muted-foreground";

export default function AdminVideoEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [lang, setLang] = useState("fr");
  const [saving, setSaving] = useState(false);
  const [transcode, setTranscode] = useState("none");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const { data: cats } = useQuery({ queryKey: ["admin-cats"], queryFn: async () => (await api.get("/admin/categories")).data });
  const { data: creators } = useQuery({ queryKey: ["admin-creators"], queryFn: async () => (await api.get("/admin/creators")).data });

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/videos/${id}`).then(({ data }) => {
      setForm({ ...empty, ...data, ecosystem: { ...empty.ecosystem, ...(data.ecosystem || {}) }, tags: data.tags || [],
        publish_at: data.publish_at || "", unpublish_at: data.unpublish_at || "" });
      setTranscode(data.transcode_status || "none");
    });
    return () => clearInterval(pollRef.current);
  }, [id, isNew]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updLoc = (k, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [lang]: v } }));
  const updEco = (k, v) => setForm((f) => ({ ...f, ecosystem: { ...f.ecosystem, [k]: v } }));

  const payload = () => ({
    ...form,
    publish_at: form.publish_at || null,
    unpublish_at: form.unpublish_at || null,
    ecosystem: Object.fromEntries(Object.entries(form.ecosystem).map(([k, v]) => [k, v || null])),
  });

  const save = async (goStatus) => {
    if (!form.title.fr) return toast.error("Le titre (FR) est requis");
    setSaving(true);
    try {
      let vid = id;
      if (isNew) {
        const { data } = await api.post("/admin/videos", payload());
        vid = data.id;
        toast.success("Vidéo créée");
        nav(`/admin/videos/${vid}`, { replace: true });
      } else {
        await api.put(`/admin/videos/${id}`, payload());
        toast.success(t("admin.save"));
      }
      if (goStatus) {
        await api.post(`/admin/videos/${vid}/status`, { status: goStatus });
        toast.success("Publiée");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const uploadThumb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("thumbnail_url", data.url);
      toast.success("Miniature téléversée");
    } catch (_) {
      toast.error("Échec du téléversement");
    }
  };

  const pollStatus = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const { data } = await api.get(`/admin/videos/${id}/transcode-status`);
      setTranscode(data.transcode_status);
      if (["ready", "errored"].includes(data.transcode_status)) {
        clearInterval(pollRef.current);
        setUploading(false);
        toast[data.transcode_status === "ready" ? "success" : "error"](
          data.transcode_status === "ready" ? "Transcodage terminé — prête à publier" : "Échec du transcodage");
      }
    }, 2000);
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post(`/admin/videos/${id}/create-upload`);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", resolveUrl(data.upload_url));
        xhr.upload.onprogress = (ev) => ev.lengthComputable && setProgress(Math.round((ev.loaded / ev.total) * 100));
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject());
        xhr.onerror = reject;
        xhr.send(file);
      });
      setTranscode("waiting");
      pollStatus();
    } catch (err) {
      setUploading(false);
      toast.error(err.response?.data?.detail || "Échec du téléversement vidéo");
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{isNew ? t("admin.newVideo") : t("admin.edit")}</h1>
        <div className="flex gap-2">
          <button onClick={() => save()} disabled={saving} data-testid="save-video" className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("admin.save")}
          </button>
          {!isNew && (
            <button onClick={() => save("published")} disabled={saving || transcode !== "ready"} data-testid="save-publish" title={transcode !== "ready" ? "La vidéo doit être prête" : ""} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90 disabled:opacity-50">
              <Send className="h-4 w-4" /> {t("admin.publish")}
            </button>
          )}
        </div>
      </div>

      {/* language tabs */}
      <div className="mt-6 flex gap-1 rounded-lg bg-white/5 p-1">
        {["fr", "en"].map((l) => (
          <button key={l} onClick={() => setLang(l)} data-testid={`lang-tab-${l}`} className={`flex-1 rounded-md py-2 text-sm font-semibold uppercase transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            {l === "fr" ? "Français" : "English"}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={lbl}>Titre ({lang.toUpperCase()})</label>
          <input className={field} value={form.title[lang]} onChange={(e) => updLoc("title", e.target.value)} data-testid="video-title" />
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Description ({lang.toUpperCase()})</label>
          <textarea rows={4} className={field} value={form.description[lang]} onChange={(e) => updLoc("description", e.target.value)} data-testid="video-desc" />
        </div>

        <div>
          <label className={lbl}>Catégorie</label>
          <select className={field} value={form.category_id || ""} onChange={(e) => upd("category_id", e.target.value)} data-testid="video-category">
            <option value="">—</option>
            {cats?.map((c) => <option key={c.id} value={c.id}>{c.name.fr}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Créateur / Partenaire</label>
          <select className={field} value={form.creator_id || ""} onChange={(e) => upd("creator_id", e.target.value)} data-testid="video-creator">
            <option value="">—</option>
            {creators?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className={lbl}>Tags (séparés par des virgules)</label>
          <input className={field} value={(form.tags || []).join(", ")} onChange={(e) => upd("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="video-tags" />
        </div>

        {/* Thumbnail */}
        <div className="md:col-span-2">
          <label className={lbl}>Miniature</label>
          <div className="flex items-center gap-4">
            <div className="flex h-24 w-40 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
              {form.thumbnail_url ? <img src={fileUrl(form.thumbnail_url)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <label className="cursor-pointer rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm transition-colors hover:bg-white/10">
              Téléverser
              <input type="file" accept="image/*" className="hidden" onChange={uploadThumb} data-testid="thumb-upload" />
            </label>
          </div>
        </div>

        {/* Curation & sponsor */}
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-elevated))] p-4">
          <p className={lbl}>Curation</p>
          <label className="flex items-center justify-between py-1.5 text-sm">À la une (featured)
            <input type="checkbox" checked={form.is_featured} onChange={(e) => upd("is_featured", e.target.checked)} data-testid="video-featured" /></label>
          <label className="flex items-center justify-between py-1.5 text-sm">Bannière héro
            <input type="checkbox" checked={form.is_hero} onChange={(e) => upd("is_hero", e.target.checked)} data-testid="video-hero" /></label>
        </div>
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-elevated))] p-4">
          <p className={lbl}>Sponsoring</p>
          <label className="flex items-center justify-between py-1.5 text-sm">Contenu sponsorisé
            <input type="checkbox" checked={form.is_sponsored} onChange={(e) => upd("is_sponsored", e.target.checked)} data-testid="video-sponsored" /></label>
          {form.is_sponsored && (
            <input className={`${field} mt-2`} placeholder="Nom du sponsor" value={form.sponsor_name || ""} onChange={(e) => upd("sponsor_name", e.target.value)} data-testid="video-sponsor-name" />
          )}
        </div>

        {/* Scheduling */}
        <div>
          <label className={lbl}>Publication programmée</label>
          <input type="datetime-local" className={field} value={form.publish_at ? form.publish_at.slice(0, 16) : ""} onChange={(e) => upd("publish_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="video-publish-at" />
        </div>
        <div>
          <label className={lbl}>Dépublication programmée</label>
          <input type="datetime-local" className={field} value={form.unpublish_at ? form.unpublish_at.slice(0, 16) : ""} onChange={(e) => upd("unpublish_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="video-unpublish-at" />
        </div>

        {/* Ecosystem hooks */}
        <div className="md:col-span-2 rounded-lg border border-white/5 bg-[hsl(var(--surface-elevated))] p-4">
          <p className={lbl}>Liens écosystème (optionnels)</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input className={field} placeholder="partner_id" value={form.ecosystem.partner_id || ""} onChange={(e) => updEco("partner_id", e.target.value)} />
            <input className={field} placeholder="academy_module_id" value={form.ecosystem.academy_module_id || ""} onChange={(e) => updEco("academy_module_id", e.target.value)} />
            <input className={field} placeholder="event_id" value={form.ecosystem.event_id || ""} onChange={(e) => updEco("event_id", e.target.value)} />
            <input className={field} placeholder="campaign_id" value={form.ecosystem.campaign_id || ""} onChange={(e) => updEco("campaign_id", e.target.value)} />
            <input className={`${field} md:col-span-2`} placeholder="URL CTA externe" value={form.ecosystem.external_cta_url || ""} onChange={(e) => updEco("external_cta_url", e.target.value)} />
          </div>
        </div>
      </div>

      {/* Upload pipeline */}
      <div className="mt-6 rounded-xl border border-primary/15 bg-primary/5 p-5">
        <div className="flex items-center gap-2">
          <UploadCloud className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Fichier vidéo (pipeline Mux)</h2>
        </div>
        {isNew ? (
          <p className="mt-2 text-sm text-muted-foreground">Enregistrez d'abord la vidéo pour activer le téléversement.</p>
        ) : (
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="cursor-pointer rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90">
                Téléverser une vidéo
                <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={uploading} data-testid="video-file-upload" />
              </label>
              <span className="text-sm text-muted-foreground">Statut : <b className="text-foreground" data-testid="transcode-status">{transcode}</b></span>
              {transcode === "ready" && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
            </div>
            {(uploading || progress > 0) && progress < 100 && (
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
            {["waiting", "preparing"].includes(transcode) && (
              <p className="mt-2 flex items-center gap-2 text-sm text-amber-400"><Loader2 className="h-4 w-4 animate-spin" /> Transcodage en cours…</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
